/**
 * Plan value for a student who is not on an IEP or 504, but still has
 * accommodations staff need to see (SST, RTI, MTSS, and similar).
 *
 * The roster tag stays "Other". The reason lives in planNote and is shown
 * only as a hover tooltip. The case manager is the 504 coordinator
 * (role admin_504). These students are not on the meeting calendar.
 */

export const PLAN_OTHER = 'Other'
export const PLAN_NOTE_MAX = 200

/** School-wide All lists: IEP, 504, and Other. Blank or unknown plans stay off. */
export function isAllScopePlan(plan) {
  const value = String(plan || '').trim()
  return value === 'IEP' || value === '504' || value === PLAN_OTHER
}

export function isOtherPlan(studentOrPlan) {
  const plan =
    typeof studentOrPlan === 'string'
      ? studentOrPlan
      : (studentOrPlan?.app?.studentData?.plan ?? studentOrPlan?.plan ?? '')
  return String(plan || '').trim() === PLAN_OTHER
}

/** Note stored for an Other plan. Empty for every other plan. */
export function normalizePlanNote(plan, note) {
  if (String(plan || '').trim() !== PLAN_OTHER) return ''
  return String(note || '')
    .trim()
    .slice(0, PLAN_NOTE_MAX)
}

/**
 * Tooltip text for the roster tag. Uses the same display priority as the
 * plan tag (overrides, then app data). Empty when there is nothing to show.
 * @param {object} student
 * @param {(student: object, field: string, fallback?: string) => string} read
 */
export function displayOtherPlanNote(student, read) {
  if (!student || typeof read !== 'function') return ''
  if (read(student, 'plan', '') !== PLAN_OTHER) return ''
  const note = read(student, 'planNote', '')
  if (note == null || note === '' || note === '—') return ''
  return String(note).trim()
}

/**
 * Case manager to keep when the plan is Other.
 * A matching 504 coordinator stays. One coordinator is filled in automatically.
 * Several coordinators and a non-match clear the field so someone has to pick.
 * An empty coordinator list leaves the current id alone — the save check
 * reports that a 504 coordinator has to exist first.
 * @returns {string}
 */
export function suggestedOtherCaseManagerId(currentId, caseManagers = []) {
  const coordinators = (caseManagers || []).filter((cm) => cm?.role === 'admin_504' && cm.id)
  if (!coordinators.length) return currentId || ''
  if (coordinators.some((cm) => String(cm.id) === String(currentId || ''))) return currentId || ''
  if (coordinators.length === 1) return coordinators[0].id
  return ''
}

/** Empty when the assignment is valid. A message when an Other plan cannot be saved. */
export function otherPlanCaseManagerError(plan, caseManagerId, caseManagers = []) {
  if (String(plan || '').trim() !== PLAN_OTHER) return ''
  const coordinators = (caseManagers || []).filter((cm) => cm?.role === 'admin_504' && cm.id)
  if (!coordinators.length) {
    return 'Other plans need a 504 coordinator. Add that role before saving this student.'
  }
  const assigned = coordinators.some((cm) => String(cm.id) === String(caseManagerId || ''))
  if (!assigned) return 'The case manager for an Other plan must be the 504 coordinator.'
  return ''
}
